
import { RebarShapeDefinition } from './types';
import * as THREE from 'three';

export class RebarShapeManager {
    private static shapes: Map<string, RebarShapeDefinition> = new Map();

    static {
        this.initializeStandardShapes();
    }

    private static initializeStandardShapes() {
        // Shape 00: Straight Bar
        this.registerShape({
            id: '00',
            hooks: { start: 'None', end: 'None' },
            segments: [
                { lengthFormula: 'A', direction: [1, 0, 0] }
            ]
        });

        // Shape 11: L-Bar (90 deg bend)
        this.registerShape({
            id: '11',
            hooks: { start: 'None', end: 'None' },
            segments: [
                { lengthFormula: 'A', direction: [1, 0, 0] },
                { lengthFormula: 'B', direction: [0, 1, 0] }
            ]
        });

        // Shape 21: U-Bar (Stirrup Open)
        this.registerShape({
            id: '21',
            hooks: { start: 'None', end: 'None' },
            segments: [
                { lengthFormula: 'A', direction: [0, 1, 0] },
                { lengthFormula: 'B', direction: [1, 0, 0] },
                { lengthFormula: 'C', direction: [0, -1, 0] }
            ]
        });

        // Shape 51: Closed Stirrup (Rectangular)
        this.registerShape({
            id: '51',
            hooks: { start: 'Standard 135', end: 'Standard 135' },
            segments: [
                { lengthFormula: 'A', direction: [1, 0, 0] }, // Bottom
                { lengthFormula: 'B', direction: [0, 1, 0] }, // Right
                { lengthFormula: 'C', direction: [-1, 0, 0] }, // Top
                { lengthFormula: 'D', direction: [0, -1, 0] }  // Left
            ]
        });
    }

    static registerShape(shape: RebarShapeDefinition) {
        this.shapes.set(shape.id, shape);
    }

    static getShape(id: string): RebarShapeDefinition | undefined {
        return this.shapes.get(id);
    }

    /**
     * Resolve segment lengths based on Host Dimensions and Cover.
     * Simple layout algorithm: Fits shape within a bounding box.
     */
    static resolveShapeGeometry(shapeId: string, hostSize: THREE.Vector3, cover: number): { points: THREE.Vector3[] } {
        const shape = this.getShape(shapeId);
        if (!shape) return { points: [] };

        const points: THREE.Vector3[] = [];
        let currentPos = new THREE.Vector3(0, 0, 0);
        points.push(currentPos.clone());

        // Effective size for rebar cage
        const effectiveWidth = hostSize.x - 2 * cover;
        const effectiveDepth = hostSize.y - 2 * cover;
        // const effectiveHeight = hostSize.z - 2 * cover;

        // Naive interpretation of "A", "B", "C" mapping to Box dimensions
        // For Stirrup (51): A=Width, B=Depth

        const params: Record<string, number> = {
            'A': effectiveWidth,
            'B': effectiveDepth,
            'C': effectiveWidth,
            'D': effectiveDepth
        };

        shape.segments.forEach(seg => {
            const len = params[seg.lengthFormula || 'A'] || 1.0; // Default 1m if unknown
            const dir = new THREE.Vector3(...seg.direction);

            const nextPos = currentPos.clone().add(dir.multiplyScalar(len));
            points.push(nextPos);
            currentPos = nextPos;
        });

        // Center the shape? 
        // Currently generating from (0,0,0). Caller needs to offset to fit in Host.

        return { points };
    }
}
